import { ReebeloStoreT } from '@lambda/reebelo';
import classNames from 'classnames';
import { Check, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { Dispatch, memo, SetStateAction, useState } from 'react';
import { priceFormatSuperscript } from '@/lib/price';
import { CartWarrantyT } from '@/lib/reebelocare/types';
import ShieldIcon from '@/public/icons/reebelocare/cart.svg';
import settings, { priceFormater } from '@/settings';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

export const INFO_DATA: Partial<Record<ReebeloStoreT, string[]>> & {
  default: string[];
} = {
  'reebelo-au': [
    'Replace or refresh your device for any reason (service fees apply)',
    'Includes support for cracked screens, liquid spills and battery faults',
  ],
  'reebelo-nz': [
    'Repair for any reason incl cracked screens, liquid spills and battery issues',
    "Like-for-like replacement if we can't repair it",
  ],
  'reebelo-us': [
    'Covers damage & defects incl. cracked screen, liquid spills and battery',
    'Get fast, on-demand repair at over 1,000 locations',
  ],
  'reebelo-ca': [
    'Covers damage & defects incl. cracked screen, liquid spills and battery',
    'Online claims & over-the-phone support',
  ],
  default: [
    'Covers damage & defects incl. cracked screen, liquid spills and battery',
    'Get fast, on-demand repair at over 1,000 locations',
  ],
};

export const SECTION_TITLES: Partial<Record<ReebeloStoreT, string>> & {
  default: string;
} = {
  'reebelo-au': 'Add 24-Month ReebeloCare Plan',
  'reebelo-nz': '24-Month Device Protection',
  'reebelo-us': '24-Month Device Protection',
  'reebelo-ca': '24-Month Device Protection',
  default: '24-Month Device Protection',
};

const Toggle = memo(
  ({
    reebeloCareChecked,
    setReebeloCareChecked,
  }: {
    reebeloCareChecked: boolean;
    setReebeloCareChecked: Dispatch<SetStateAction<boolean>>;
  }) => (
    <button
      id="e2e-pdp-add-reebelocare"
      aria-label={
        reebeloCareChecked ? t`Remove ReebeloCare` : t`Add ReebeloCare`
      }
      onClick={() => setReebeloCareChecked((prev) => !prev)}
      className="z-10 flex h-5 w-10 shrink-0 items-center justify-end rounded-full px-0.5 transition-all duration-150 ease-in-out focus:outline-none"
      // eslint-disable-next-line
      style={{
        background: reebeloCareChecked
          ? '#128452'
          : 'linear-gradient(291deg, rgba(66, 232, 238, 0.10) 5.26%, rgba(43, 212, 93, 0.10) 96.32%), rgba(0, 0, 0, 0.15)',
      }}
    >
      <div
        className={classNames(
          'h-3.5 w-3.5 rounded-full bg-white drop-shadow transition-all duration-150 ease-in-out',
          { 'mr-1.5 -translate-x-full': !reebeloCareChecked },
        )}
      />
    </button>
  ),
);

Toggle.displayName = 'Toggle';

const Header = memo(
  ({
    warranties,
    expanded,
    setExpanded,
    reebeloCareChecked,
    setReebeloCareChecked,
    totalReebeloCarePrice,
    isMobile,
  }: {
    warranties: CartWarrantyT;
    expanded: boolean;
    setExpanded: Dispatch<SetStateAction<boolean>>;
    reebeloCareChecked: boolean;
    setReebeloCareChecked: Dispatch<SetStateAction<boolean>>;
    totalReebeloCarePrice: number;
    isMobile: boolean;
  }) => {
    const count = Object.keys(warranties).length;

    const title = t(SECTION_TITLES?.[settings.store] ?? SECTION_TITLES.default);

    return (
      <div className="flex items-center justify-between gap-2">
        <div
          className={classNames('flex items-center gap-2', {
            'w-full flex-col justify-center text-center': isMobile,
          })}
        >
          <div className="h-6 w-6">
            <Image
              src={ShieldIcon}
              alt={t`Shield Icon`}
              width={24}
              height={24}
              className="h-full w-full"
            />
          </div>
          <div>
            <h3
              className={classNames('font-bold', {
                'justify-center': isMobile,
              })}
            >
              {title}
            </h3>
            <div
              className={classNames('mt-1 flex items-center gap-2', {
                'justify-center': isMobile,
              })}
            >
              <span className="text-[#4A5565]">
                {priceFormatSuperscript(
                  priceFormater.long(totalReebeloCarePrice),
                )}
              </span>
              <button
                className="flex items-center gap-1 text-[#99A1AF] focus:outline-none"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {count !== 1
                  ? t`${{ count }} devices covered`
                  : t`${{ count }} device covered`}
                <ChevronDown
                  className={classNames(
                    'h-4 w-4 transition-transform duration-150',
                    { 'rotate-180': expanded },
                  )}
                  strokeWidth={2}
                />
              </button>
            </div>
          </div>
        </div>
        {!isMobile && (
          <Toggle
            reebeloCareChecked={reebeloCareChecked}
            setReebeloCareChecked={setReebeloCareChecked}
          />
        )}
      </div>
    );
  },
);

Header.displayName = 'Header';

const CoveredDevicesList = memo(
  ({
    warranties,
    expanded,
  }: {
    warranties: CartWarrantyT;
    expanded: boolean;
  }) => {
    const { cartLines } = useCartContext();

    return (
      <div
        className={classNames(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          {
            'grid-rows-[1fr]': expanded,
            'grid-rows-[0fr]': !expanded,
          },
        )}
      >
        <div className="overflow-hidden">
          <ul className="mt-3 space-y-1 divide-y divide-[#F2F2F2] rounded-xl border border-[#F2F2F2] bg-white">
            {Object.entries(warranties).map(([lineId, { productWarranty }]) => {
              const device = cartLines.find((l) => l.id === lineId);

              if (!device) {
                return null;
              }

              return (
                <li
                  key={lineId}
                  className="flex items-center justify-between gap-x-4 p-3"
                >
                  <div className="flex items-center gap-x-2 truncate">
                    {device.image && (
                      <div className="h-8 w-8 shrink-0">
                        <Image
                          src={device.image}
                          alt={device.title}
                          width={40}
                          height={40}
                          className="mr-2 inline-block h-full w-full object-contain"
                        />
                      </div>
                    )}
                    <span className="truncate">{device.title}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span>{device.quantity} x </span>
                    <span className="font-medium" translate="no">
                      {priceFormatSuperscript(
                        priceFormater.long(productWarranty.price),
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  },
);

CoveredDevicesList.displayName = 'CoveredDevicesList';

const Divider = memo(() => (
  <div className="my-3 h-px w-full bg-[rgba(179,_213,_203,_0.50)]" />
));

Divider.displayName = 'Divider';

export const InfoFooter = memo(
  ({
    onShowWarrantyDetails,
    isMobile,
    neutralLinkHover = false,
    italic = true,
  }: {
    onShowWarrantyDetails?: () => void;
    isMobile: boolean;
    /** Document links darken on hover like Plan Summary, not turn blue. */
    neutralLinkHover?: boolean;
    italic?: boolean;
  }) => {
    const linkClass = neutralLinkHover
      ? 'underline hover:text-gray-700'
      : 'underline hover:text-[#3C83F6]';

    if (settings.store === 'reebelo-au') {
      return (
        <div
          className={classNames('mt-3 text-[11px] text-[#4A5565B2]', {
            italic,
            'text-center': isMobile,
          })}
        >
          {t`By buying, you agree to the`}{' '}
          <a
            className={linkClass}
            href="https://edge.reebelo.com/files/rmg/rc.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t`T&Cs`}
          </a>{' '}
          {t`and confirm you fit within the`}{' '}
          <a
            className={linkClass}
            href="https://edge.reebelo.com/files/rmg/tmd.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t`Target Market`}
          </a>
          . {t`View`}{' '}
          <button
            className={classNames('underline hover:text-gray-700', { italic })}
            onClick={onShowWarrantyDetails}
          >
            {t`Plan Summary`}
          </button>
        </div>
      );
    }

    if (settings.store === 'reebelo-nz') {
      return (
        <div
          className={classNames('mt-3 text-[11px] text-[#4A5565B2]', {
            italic,
            'text-center': isMobile,
          })}
        >
          {t`By buying, you agree to the`}{' '}
          <a
            className={linkClass}
            href="https://edge.reebelo.com/files/rmg/rc-nz.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t`T&Cs`}
          </a>
          . {t`View`}{' '}
          <button
            className={classNames('underline hover:text-gray-700', { italic })}
            onClick={onShowWarrantyDetails}
          >
            {t`Plan Summary`}
          </button>
          .
        </div>
      );
    }

    if (settings.store === 'reebelo-us') {
      return (
        <div
          className={classNames('mt-3 text-[11px] text-[#4A5565B2]', {
            italic,
            'text-center': isMobile,
          })}
        >
          {t`By buying you agree to the`}{' '}
          <a
            className={linkClass}
            href="https://reebelo.com/policies/warranty-and-refund-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t`T&Cs`}
          </a>
          . {t`View`}{' '}
          <button
            className={classNames('underline hover:text-gray-700', { italic })}
            onClick={onShowWarrantyDetails}
          >
            {t`Plan Summary`}
          </button>
          .
        </div>
      );
    }

    if (settings.store === 'reebelo-ca') {
      return (
        <div
          className={classNames('mt-3 text-[11px] text-[#4A5565B2]', {
            italic,
            'text-center': isMobile,
          })}
        >
          <div>
            {t`By buying, you agree to the T&Cs (`}{' '}
            <a
              href="https://edge.reebelo.com/files/sqt/PDS-CA.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t`EN`}
            </a>{' '}
            |{' '}
            <a
              href="https://edge.reebelo.com/files/sqt/PDS-FRCA.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t`FR`}
            </a>
            ). {t`View`}{' '}
            <button
              className={classNames('underline hover:text-gray-700', {
                italic,
              })}
              onClick={onShowWarrantyDetails}
            >
              {t`Plan Summary`}
            </button>
          </div>
          <div className="mt-2">
            {t`Notice for Quebec residents:`}{' '}
            <a
              className={linkClass}
              href="https://edge.reebelo.com/files/sqt/IWI-CA.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t`Important Warranty Information / Avis important sur la garantie légale`}
            </a>
            .
          </div>
        </div>
      );
    }

    return null;
  },
);

InfoFooter.displayName = 'InfoFooter';

const WarrantyDetails = memo(() => {
  const data = INFO_DATA?.[settings.store] ?? INFO_DATA.default;

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div className="flex items-start gap-2" key={index}>
          <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
          <span>{t(item)}</span>
        </div>
      ))}
    </div>
  );
});

WarrantyDetails.displayName = 'WarrantyDetails';

const MobileCtas = ({
  isCheckingOut = false,
  onCheckoutClick,
}: {
  isCheckingOut?: boolean;
  onCheckoutClick: (reebeloCareChecked: boolean) => void;
}) => {
  const { cartLoading } = useCartContext();

  const loading = cartLoading || isCheckingOut;

  return (
    <div className="mt-4 space-y-2">
      <button
        disabled={loading}
        onClick={() => onCheckoutClick(true)}
        className="flex h-12 w-full items-center justify-center rounded-lg bg-gray-700 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t`Add ReebeloCare & Checkout`}
      </button>
      <button
        disabled={loading}
        onClick={() => onCheckoutClick(false)}
        className="disabed:opacity-50 flex h-12 w-full items-center justify-center rounded-lg border border-gray-700 font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t`No Thanks, Proceed to Checkout`}
      </button>
    </div>
  );
};

const ReebeloCarePanel = memo(
  ({
    warranties,
    reebeloCareChecked,
    setReebeloCareChecked,
    totalReebeloCarePrice,
    isMobile = false,
    onCheckoutClick,
    isCheckingOut = false,
    className,
    onShowWarrantyDetails,
  }: {
    warranties: CartWarrantyT;
    reebeloCareChecked: boolean;
    setReebeloCareChecked: Dispatch<SetStateAction<boolean>>;
    totalReebeloCarePrice: number;
    isMobile?: boolean;
    onCheckoutClick?: (reebeloCareChecked: boolean) => void;
    isCheckingOut?: boolean;
    className?: string;
    onShowWarrantyDetails?: () => void;
  }) => {
    const [expanded, setExpanded] = useState(false);

    if (Object.keys(warranties).length > 0) {
      return (
        <div
          className={classNames(
            'rounded-xl border border-[#D9E3DF] bg-[linear-gradient(308deg,_#FFF_48.09%,_#F2FFF9_104.3%)] p-3 text-[13px] leading-tight shadow-[0_4px_4px_0_rgba(14,_78,_50,_0.10)]',
            {
              'mt-2': !isMobile,
            },
            className,
          )}
        >
          <Header
            warranties={warranties}
            expanded={expanded}
            setExpanded={setExpanded}
            reebeloCareChecked={reebeloCareChecked}
            setReebeloCareChecked={setReebeloCareChecked}
            totalReebeloCarePrice={totalReebeloCarePrice}
            isMobile={isMobile}
          />
          <CoveredDevicesList warranties={warranties} expanded={expanded} />
          <Divider />
          <WarrantyDetails />
          {isMobile && onCheckoutClick && (
            <MobileCtas
              isCheckingOut={isCheckingOut}
              onCheckoutClick={onCheckoutClick}
            />
          )}
          <InfoFooter
            onShowWarrantyDetails={onShowWarrantyDetails}
            isMobile={isMobile}
          />
        </div>
      );
    }

    return null;
  },
);

ReebeloCarePanel.displayName = 'ReebeloCarePanel';

export default ReebeloCarePanel;
